import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { NoData } from 'components/NoData'
import { getBowserDayDispenses } from 'features/fuelManagement/fuelCustodySlice'
import { OtherText } from 'styling/Typography'
import { formatNumbersForDisplay } from 'utils/helpers'
import { IFuelBowserDayDispenseRow } from 'types/fuelCustody.interface'
import { RootState } from 'store'
import { BreakdownCellText, BreakdownTitle, BreakdownWrap } from './BowserDayBreakdown.styled'

const TOTAL_ROW_ID = 0

interface IBowserDayBreakdownProps {
  bowserId: number
  dateISO: string
}

const columns: IHonestTableColumn<IFuelBowserDayDispenseRow>[] = [
  {
    id: 'machine',
    header: 'Machine',
    minWidth: '9rem',
    cell: (item) => <BreakdownCellText $em={item.id === TOTAL_ROW_ID}>{item.machine}</BreakdownCellText>,
  },
  {
    id: 'time',
    header: 'Time',
    cell: (item) => (item.id === TOTAL_ROW_ID
      ? <BreakdownCellText $em />
      : <BreakdownCellText>{item.time}</BreakdownCellText>),
  },
  {
    id: 'litres',
    header: 'Litres',
    align: 'right',
    cell: (item) => (
      <BreakdownCellText $em={item.id === TOTAL_ROW_ID}>
        {`${formatNumbersForDisplay(item.litres)} L`}
      </BreakdownCellText>
    ),
  },
]

export const BowserDayBreakdown = ({ bowserId, dateISO }: IBowserDayBreakdownProps) => {
  const dispatch = useDispatch()
  const { bowserDayDispenses } = useSelector((store: RootState) => store.fuelCustody)

  useEffect(() => {
    dispatch(getBowserDayDispenses({ id: bowserId, params: { date: dateISO } }) as any)
  }, [dispatch, bowserId, dateISO])

  const rows = bowserDayDispenses ?? []
  if (!rows.length) return <NoData text='No dispenses recorded for this bowser day.' />

  const totalL = rows.reduce((sum, row) => sum + row.litres, 0)
  const tableRows: IFuelBowserDayDispenseRow[] = [
    ...rows,
    { id: TOTAL_ROW_ID, machine: 'Total', time: '', litres: totalL },
  ]

  return (
    <BreakdownWrap>
      <BreakdownTitle>Dispensed to machines</BreakdownTitle>
      <HonestTable columns={columns} data={tableRows} keyField='id' />
      <OtherText grey>Per-machine dispenses sourced from the bowser for this day.</OtherText>
    </BreakdownWrap>
  )
}
